<template>

    <div class="modal" :class="classes">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <span class="modal-title">{{ title }}</span>
                    <div class="d-flex align-items-center">
                        <slot name="subtitle"></slot>
                        <v-close @click.native="closeModal" />
                    </div>
                </div>
                <div v-if="$slots['alert']" class="modal-alert">
                    <slot name="alert"></slot>
                </div>
                <div v-if="!error && $slots['pagination']" class="modal-pagination">
                    <slot name="pagination"></slot>
                </div>
                <div v-if="canRender && $slots['nav']" class="modal-nav">
                    <slot name="nav"></slot>
                </div>
                <div class="modal-body" :class="{ padded: padded || loading }">
                    <v-loader v-if="loading" size="lg" />
                    <v-alert v-else-if="error" type="danger">{{ error }}</v-alert>
                    <slot v-else-if="$slots['body']" name="body"></slot>
                    <slot v-else></slot>
                </div>
                <div v-if="canRender && $slots['footer']" class="modal-footer">
                    <slot name="footer"></slot>
                </div>
            </div>
        </div>
    </div>

</template>

<style scoped lang="scss">

    .modal {

        border-radius: 0;
        display: block;
        height: 100%;
        left: 0;
        outline: 0;
        overflow: auto;
        padding: 0;
        position: fixed;
        top: 0;
        width: 100%;
        z-index: 10001;

        &.modal-size-sm {
            .modal-dialog {
                margin-left: calc(map-get($grid-breakpoints, 'sm') / -2);
                width: map-get($grid-breakpoints, 'sm');
            }
        }

        &.modal-size-md {
            .modal-dialog {
                margin-left: calc(map-get($grid-breakpoints, 'md') / -2);
                width: map-get($grid-breakpoints, 'md');
            }
        }

        &.modal-size-lg {
            .modal-dialog {
                margin-left: calc(map-get($grid-breakpoints, 'lg') / -2);
                width: map-get($grid-breakpoints, 'lg');
            }
        }

        &.loading {
            .modal-body {
                align-items: center;
                display: flex;
                justify-content: center;
            }
        }

        .modal-dialog {
            @include shad(2);
            -webkit-overflow-scrolling: touch;
            background: $color-white;
            border: none;
            border-radius: 5px;
            left: 50%;
            margin-top: 30px;
            max-width: 100%;
            padding: 0;
            position: absolute;
            top: 0;
            @media only screen and (max-width: map-get($grid-breakpoints, 'lg')) {
                left: 0 !important;
                margin: 0 !important;
                top: 0 !important;
                width: 100% !important;
            }
        }

        .modal-content {
            background: $color-white;
            border: none;
            display: flex;
            flex-direction: column;
        }

        .modal-header {
            align-items: center;
            background: $color-white;
            border-bottom: 1px solid $color-rule;
            box-sizing: border-box;
            display: flex;
            justify-content: space-between;
            min-height: 60px;
            padding: 10px;
            position: relative;
        }

        .modal-title {
            @include font-semibold(16px);
            align-items: center;
            color: $color-text;
            display: flex;
            line-height: normal;
            margin-left: 10px;
        }

        .modal-alert {
            border-bottom: 1px solid $color-rule-light;
            .alert {
                margin: 0;
            }
        }

        .modal-pagination {
            background: $color-white;
            border-radius: 5px;
            display: flex;
            flex-direction: column;
            gap: 5px;
            margin-right: -50px;
            padding: 10px 10px 10px 20px;
            position: absolute;
            right: 0;
            top: 0;
        }

        .modal-nav {
            @include font-medium(14px);
            align-items: center;
            background: $color-white;
            border-bottom: 1px solid $color-rule-light;
            display: flex;
            justify-content: space-between;
            padding: 10px 15px;
        }

        .modal-body {
            border-radius: 0;
            flex: 1 1 auto;
            padding: 0;
            position: relative;
            &.padded {
                padding: 30px;
            }
            text-align: center;
            > *:last-child {
                margin-bottom: 0;
            }
            p {
                @include font-medium(14px);
                color: $color-text;
                line-height: 1.6em;
                margin: 0 0 15px 0;
                text-align: center;
                &.small {
                    font-size: 13px;
                }
            }
        }

        .modal-footer {
            background: $color-white;
            border-top: 1px solid $color-rule;
            box-sizing: border-box;
            display: flex;
            justify-content: space-between;
            padding: 15px;
            position: relative;
            z-index: 1;
            *:only-child {
                margin-left: auto;
            }
        }

    }

</style>

<script>

import Close from '@cmp/Close';

export default {

    components: {
        'v-close': Close,
    },

    props: {
        title: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
        },
        error: {
            type: String,
        },
        padded: {
            type: Boolean,
            default: true,
        },
        size: {
            type: String,
            default: 'sm',
        },
        standalone: {
            type: Boolean,
            default: false,
        },
    },

    computed: {

        canRender() {
            return !this.loading && !this.error;
        },

        classes() {
            return {
                'loading': this.loading,
                'modal-size-sm': this.size === 'sm',
                'modal-size-md': this.size === 'md',
                'modal-size-lg': this.size === 'lg',
            }
        },

    },

    mounted() {
        document.addEventListener('keydown', this.handleEscapeKey);
    },

    beforeDestroy() {
        document.removeEventListener('keydown', this.handleEscapeKey);
    },

    methods: {

        handleEscapeKey(event) {
            if (event.key === 'Escape') {
                this.closeModal();
            }
        },

        closeModal() {
            if (this.standalone) {
                this.$emit('close');
            } else {
                this.$store.commit('setModal', null);
            }
        },

    },

}

</script>
